import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  TrendingUp,
  ArrowRight,
  BarChart2,
  Shuffle,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Lightbulb
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface HookVariant {
  hook: string;
  score: number;
}

const ABHookTester = () => {
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hookVariants, setHookVariants] = useState<HookVariant[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!caption.trim()) {
      setError('Please enter a caption to test');
      return;
    }

    setLoading(true);
    setError(null);
    setHookVariants([]);

    try {
      const prompt = `You are a growth marketer; Caption="${caption}"; output ONLY JSON array of two objects each with "hook" (one sentence) and "score" (0–1 confidence for higher CTR); no extra commentary.`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            const parsedData = JSON.parse(cleanedResponse);
            if (Array.isArray(parsedData) && parsedData.length > 0) {
              setHookVariants(parsedData);
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          throw error;
        }
      );
    } catch (err) {
      setError('Failed to generate hook variants. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (index: number) => {
    const variant = hookVariants[index];
    if (!variant) return;
    
    navigator.clipboard.writeText(variant.hook);
    setCopiedIndex(index);
    
    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-500 dark:text-green-400';
    if (score >= 0.6) return 'text-yellow-500 dark:text-yellow-400';
    return 'text-red-500 dark:text-red-400';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 0.8) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 0.6) return 'bg-yellow-100 dark:bg-yellow-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Average';
    return 'Needs Improvement';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">A/B Hook Tester</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Compare different hooks for your captions to maximize engagement
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Test Hook Variants</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="caption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your Caption or Post Content
            </label>
            <textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={5}
              placeholder="Enter your caption or post content to generate and test different hook variations..."
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The AI will generate two different hook variations and score them based on potential engagement.
            </p>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !caption.trim()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                loading || !caption.trim()
                  ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Shuffle className="h-5 w-5" />
                  <span>Generate Hook Variants</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>

      {/* Results */}
      {hookVariants.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <BarChart2 className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Hook Comparison
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hookVariants.map((variant, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full font-semibold text-sm">
                      {String.fromCharCode(65 + index)} {/* A, B, C, etc. */}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Hook Variant {index + 1}
                      </h3>
                      <div className="flex items-center mt-1">
                        <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${getScoreBackground(variant.score)} ${getScoreColor(variant.score)}`}>
                          {getScoreLabel(variant.score)}
                        </div>
                        <div className="ml-2 flex items-center">
                          <span className={`text-sm font-medium ${getScoreColor(variant.score)}`}>
                            {Math.round(variant.score * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleCopy(index)}
                    className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                  >
                    {copiedIndex === index ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </motion.button>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg mt-4">
                  <p className="text-gray-900 dark:text-white font-medium">"{variant.hook}"</p>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <ThumbsUp className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Strengths</span>
                    </div>
                    <div className="flex items-center">
                      <ThumbsDown className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Weaknesses</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">CTR Score</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-4">
          <Lightbulb className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Hook Writing Tips</h3>
        </div>
        <ul className="space-y-3 text-indigo-100">
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Start with a question, surprising fact, or bold statement</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Create curiosity by hinting at valuable information</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Address a specific pain point your audience experiences</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Keep it concise - aim for 10-15 words maximum</span>
          </li>
        </ul>
      </div>

      {/* A/B Testing Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800/30">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
            <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100">A/B Testing Best Practices</h3>
            <p className="text-blue-800 dark:text-blue-200">Optimize your content through testing</p>
          </div>
        </div>
        <p className="text-blue-700 dark:text-blue-300 mb-4">
          A/B testing is a powerful way to improve your content performance. Here are some best practices:
        </p>
        <ul className="space-y-2 text-blue-700 dark:text-blue-300">
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Test one variable at a time for clear results</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Run tests with a large enough sample size (at least 100 views per variant)</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Test at the same time of day/week to control for timing variables</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Document your results and apply learnings to future content</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default ABHookTester;